/**
 * Advanced Question Bank: Domain 5 — Working Memory & Retention (الذاكرة العاملة والاستبقاء)
 * 15 Psychometrically Rigorous Generative Template Families (Working Memory Capacity Gwm / Cognitive Load)
 */

export const ADVANCED_MEMORY_TEMPLATES = [
  // 1. Visuospatial Mental Path Tracing (Mental Sketchpad)
  {
    id: 'mem_visuospatial_sketchpad_path',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Start at (0,0). Step 1: Up dy1, Step 2: Right dx1, Step 3: Down dy2, Step 4: Left dx2
      const up = prng.int(2, 4);
      const right = prng.int(3, 5);
      const down = prng.int(1, 2);
      const left = prng.int(1, 2);

      const finalX = right - left;
      const finalY = up - down;

      const correctCoord = `(${finalX} , ${finalY})`;
      const cand = [
        `(${finalX + 1} , ${finalY})`,
        `(${finalX - 1} , ${finalY})`,
        `(${finalX} , ${finalY + 1})`,
        `(${finalX} , ${finalY - 1})`,
        `(${finalX + 1} , ${finalY + 1})`
      ];
      const distractors = cand.filter(c => c !== correctCoord).slice(0, 3);

      const all = prng.shuffle([correctCoord, ...distractors]);
      const correctIndex = all.indexOf(correctCoord);

      const options = all.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `الإحداثي: ${c}`,
        text_en: `Coordinate: ${c}`,
        value: c
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتبع المسار التالي ذهنيًا خطوة بخطوة في مخيلتك المكانية دون رسم ورقي:\n• ابدأ من نقطة الأصل (0 , 0).\n• تحرك للأعلى بمقدار ${up} وحدات.\n• ثم تحرك لليمين بمقدار ${right} وحدات.\n• ثم تحرك للأسفل بمقدار ${down} وحدات.\n• وأخيرًا تحرك لليسار بمقدار ${left} وحدات.\n\nما هو الإحداثي الدقيق للنقطة النهائية التي استقررت عندها في الفضاء (X , Y)؟`,
        questionText_en: `Trace the path mentally on a Cartesian grid without external aids:\n• Start at origin (0 , 0).\n• Move UP by ${up} units.\n• Move RIGHT by ${right} units.\n• Move DOWN by ${down} units.\n• Move LEFT by ${left} units.\n\nWhat is your final coordinates (X , Y)?`,
        options,
        correctIndex,
        explanation_ar: `حساب الإحداثيات ذهنيًا: على المحور الأفقي X: التحرك يمينًا (${right}) ثم يسارًا (${left}) يعطي X = ${right} - ${left} = ${finalX}. على المحور الرأسي Y: التحرك لأعلى (${up}) ثم لأسفل (${down}) يعطي Y = ${up} - ${down} = ${finalY}. النتيجة النهائية هي ${correctCoord} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Tracking mental coordinates: X = +${right} - ${left} = ${finalX}, Y = +${up} - ${down} = ${finalY}, establishing ${correctCoord} (${options[correctIndex].id}).`
      };
    }
  },

  // 2. Reverse Digit Sort with Odd/Even Segregation
  {
    id: 'mem_reverse_digit_sort',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 5 digits presented. Mental task: Extract all even digits and sort them ascending
      const digits = prng.sample([1, 2, 3, 4, 5, 6, 7, 8, 9], 5);
      const evens = digits.filter(d => d % 2 === 0).sort((a, b) => a - b);
      if (evens.length < 2) evens.push(2, 4);

      const correctStr = evens.join(' - ');
      const cand = [
        evens.slice().reverse().join(' - '),
        evens.map(d => (d + 2) % 10).sort((a, b) => a - b).join(' - '),
        digits.slice(0, evens.length).sort((a, b) => b - a).join(' - '),
        evens.map(d => (d + 4) % 10).sort((a, b) => a - b).join(' - '),
        evens.map(d => (d + 6) % 10).sort((a, b) => a - b).join(' - ')
      ];
      const seen = new Set([correctStr]);
      const distractors = [];
      for (const d of cand) {
        if (!seen.has(d)) {
          seen.add(d);
          distractors.push(d);
          if (distractors.length === 3) break;
        }
      }

      const all = prng.shuffle([correctStr, ...distractors]);
      const correctIndex = all.indexOf(correctStr);

      const options = all.map((str, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: str,
        text_en: str,
        value: str
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احفظ الأرقام الخمسة التالية في ذاكرتك العاملة، ثم قم ذهنيًا بفرز الأرقام الزوجية فقط وترتيبها تصاعديًا من الأصغر للأكبر:\n\n[ ${digits.join(' , ')} ]`,
        questionText_en: `Hold the 5 digits in working memory, filter out only the EVEN numbers, and mentally sort them ascending:\n\n[ ${digits.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `الأرقام الزوجية الموجودة في القائمة هي (${evens.join(' و ')}). عند ترتيبها تصاعديًا ينتج: [ ${correctStr} ] (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Filtering even digits yields (${evens.join(', ')}). Sorting ascending gives [ ${correctStr} ] (${options[correctIndex].id}).`
      };
    }
  },

  // 3. Dual-Task Operation Span (OSpan)
  {
    id: 'mem_operation_span_interleaved',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Equation 1: (3 * 2) + 1 = 7 (True) -> Word: قمر
      // Equation 2: (8 / 2) - 1 = 2 (False) -> Word: شمس
      // Equation 3: (5 * 2) - 3 = 7 (True) -> Word: نجم
      const words = ['قمر (Moon)', 'شمس (Sun)', 'نجم (Star)', 'بحر (Sea)'];
      const correctOrder = words.map(w => w.split(' ')[0]).join(' ⬅️ ');

      const options = [
        { id: 'A', text_ar: correctOrder, text_en: 'Moon ⬅️ Sun ⬅️ Star ⬅️ Sea', isCorrect: true },
        { id: 'B', text_ar: 'شمس ⬅️ قمر ⬅️ بحر ⬅️ نجم', text_en: 'Sun ⬅️ Moon ⬅️ Sea ⬅️ Star', isCorrect: false },
        { id: 'C', text_ar: 'نجم ⬅️ شمس ⬅️ قمر ⬅️ بحر', text_en: 'Star ⬅️ Sun ⬅️ Moon ⬅️ Sea', isCorrect: false },
        { id: 'D', text_ar: 'بحر ⬅️ نجم ⬅️ شمس ⬅️ قمر', text_en: 'Sea ⬅️ Star ⬅️ Sun ⬅️ Moon', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار سعة الذاكرة العاملة المزدوجة (Operation Span)، تحل المعادلات الرياضية بالتوازي مع حفظ الكلمات المعلقة:\n1. (4 × 2) - 1 = 7 [صحيحة] ⬅️ احتفظ بكلمة: قمر\n2. (9 ÷ 3) + 2 = 5 [صحيحة] ⬅️ احتفظ بكلمة: شمس\n3. (5 × 3) - 4 = 11 [صحيحة] ⬅️ احتفظ بكلمة: نجم\n4. (6 + 4) ÷ 2 = 5 [صحيحة] ⬅️ احتفظ بكلمة: بحر\n\nما هو الترتيب التسلسلي الدقيق للكلمات الأربع بالترتيب الزمني الصحيح لعرضها؟`,
        questionText_en: `In the dual-task Operation Span paradigm:\n1. (4 * 2) - 1 = 7 [True] ⬅️ Retain: Moon\n2. (9 / 3) + 2 = 5 [True] ⬅️ Retain: Sun\n3. (5 * 3) - 4 = 11 [True] ⬅️ Retain: Star\n4. (6 + 4) / 2 = 5 [True] ⬅️ Retain: Sea\n\nWhat is the exact chronological sequence of the retained words?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `التسلسل الزمني الأصلي للكلمات المستهدفة هو: قمر ثم شمس ثم نجم ثم بحر، وهو ما يطابق بدقة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Chronological serial recall preserves the sequence Moon -> Sun -> Star -> Sea, strictly confirming (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 4. Running Memory Span (Updating Task)
  {
    id: 'mem_updating_running_span',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Long stream of 8 letters: user must recall strictly the LAST 4 letters
      const alphabet = ['أ', 'ب', 'ت', 'ث', 'ج', 'ح', 'خ', 'د', 'ر', 'ز', 'س'];
      const stream = prng.sample(alphabet, 8);
      const last4 = stream.slice(-4);
      const correctStr = last4.join(' - ');

      const dist1 = stream.slice(-5, -1).join(' - '); // Off by 1 position
      const dist2 = last4.slice().reverse().join(' - '); // Reversed
      const dist3 = stream.slice(0, 4).join(' - '); // First 4 instead of last 4

      const all = prng.shuffle([correctStr, dist1, dist2, dist3]);
      const correctIndex = all.indexOf(correctStr);

      const options = all.map((str, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: str,
        text_en: str,
        value: str
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة تحديث الذاكرة السريعة (Running Memory Updating Task)، عُرض أمامك دفق تسلسلي طويل من الحروف:\n\n[ ${stream.join(' , ')} ]\n\nالمطلوب هو استرجاع الحروف الأربعة الأخيرة فقط بالترتيب الصحيح من اليمين لليسار:`,
        questionText_en: `In the Running Memory Updating Task, a rapid stream of letters is delivered:\n\n[ ${stream.join(' , ')} ]\n\nRetrieve strictly the LAST 4 letters in correct order:`,
        options,
        correctIndex,
        explanation_ar: `الحروف الأربعة الأخيرة في نهاية الدفق هي: [ ${correctStr} ]، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `Extracting the final 4 terminal tokens yields [ ${correctStr} ] (${options[correctIndex].id}).`
      };
    }
  },

  // 5. Alphanumeric Trail Alternating Span
  {
    id: 'mem_alphanumeric_alternating',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Trail pattern: A - 1, B - 2, C - 3, D - 4, E - 5, [F - 6]
      const options = [
        { id: 'A', text_ar: 'و - 6 (F - 6)', text_en: 'F - 6', isCorrect: true },
        { id: 'B', text_ar: 'ز - 7 (G - 7)', text_en: 'G - 7', isCorrect: false },
        { id: 'C', text_ar: 'و - 5 (F - 5)', text_en: 'F - 5', isCorrect: false },
        { id: 'D', text_ar: 'هـ - 6 (E - 6)', text_en: 'E - 6', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتطلب مهمة التبديل الإدراكي المزدوج (Cognitive Flexibility Switching) التناوب بين الأبجدية والأرقام:\n\n[ أ - 1 ، ب - 2 ، ج - 3 ، د - 4 ، هـ - 5 ، ؟ ]\n\nما هو الزوج المكمل التالي في السلسلة؟`,
        questionText_en: `In the task-switching alternating trail:\n\n[ A - 1 , B - 2 , C - 3 , D - 4 , E - 5 , ? ]\n\nWhat is the next alphanumeric pair?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الحرف التالي بعد حرف (هـ) في الترتيب الأبجدي هو (و / F)، والعدد التالي بعد (5) هو (6)، إذن الزوج هو "و - 6" (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `Alternating letter progression leads to F / و and numerical step to 6, confirming (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 6. Cumulative Running Mental Tally
  {
    id: 'mem_cumulative_mental_sum',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const start = prng.int(10, 20);
      const op1 = prng.pick([+7, +8, +9]);
      const op2 = prng.pick([-4, -5, -6]);
      const op3 = prng.pick([+11, +12]);
      const op4 = prng.pick([-7, -8]);

      const correctVal = start + op1 + op2 + op3 + op4;
      const distractors = [correctVal - 1, correctVal + 2, correctVal - 2];
      const all = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = all.indexOf(correctVal);

      const options = all.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احتفظ بالرصيد التراكمي في ذاكرتك العاملة وعدّله ذهنيًا فورًا مع كل خطوة دون استخدام قلم:\n• الرصيد المبدئي: ${start}\n• أضف: ${op1}\n• اطرح: ${Math.abs(op2)}\n• أضف: ${op3}\n• اطرح: ${Math.abs(op4)}\n\nما هو الرصيد الإجمالي النهائي الدقيق؟`,
        questionText_en: `Maintain a continuous mental tally through real-time delta updates:\n• Initial: ${start}\n• Add: ${op1}\n• Subtract: ${Math.abs(op2)}\n• Add: ${op3}\n• Subtract: ${Math.abs(op4)}\n\nWhat is the final tally?`,
        options,
        correctIndex,
        explanation_ar: `العمليات التراكمية: ${start} + ${op1} = ${start + op1} ⬅️ - ${Math.abs(op2)} = ${start + op1 + op2} ⬅️ + ${op3} = ${start + op1 + op2 + op3} ⬅️ - ${Math.abs(op4)} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Executing running updates yields terminal sum ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 7. Bracket Nesting Stack Depth Tracking
  {
    id: 'mem_hierarchical_bracket_stack',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // String with nested brackets: [ ( { [ ] } ) ] -> max depth 4
      const pattern = '[ ( { [ x ] } ) ]';
      const maxDepth = 4;

      const options = [
        { id: 'A', text_ar: '4 مستويات تداخل', text_en: '4 nesting levels', value: 4 },
        { id: 'B', text_ar: '3 مستويات فقط', text_en: '3 nesting levels', value: 3 },
        { id: 'C', text_ar: '5 مستويات كاملة', text_en: '5 nesting levels', value: 5 },
        { id: 'D', text_ar: 'مستويان متوازيان', text_en: '2 parallel levels', value: 2 }
      ];

      const correctIndex = 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتبع عمق بنية مكدس الأقواس المتداخلة (Bracket Stack Depth) في التعبير التالي:\n\n[ ( { [ x ] } ) ]\n\nما هو أقصى عمق تداخل هرمي (Peak Stack Depth) وصلت إليه الذاكرة العاملة قبل إغلاق الأقواس؟`,
        questionText_en: `Track the hierarchical bracket stack depth in the expression:\n\n[ ( { [ x ] } ) ]\n\nWhat is the peak nesting depth reached before closures begin?`,
        options,
        correctIndex,
        explanation_ar: `تم فتح أربعة أقواس متتالية دون إغلاق: القوس المربع [ (عمق 1)، ثم الدائري ( (عمق 2)، ثم المعقوف { (عمق 3)، ثم المربع الداخلي [ (عمق 4). إذن أقصى عمق تداخل هو 4 (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Pushing 4 successive open brackets before any closing pop achieves a peak depth of 4 (${options[correctIndex].id}).`
      };
    }
  },

  // 8. 3x3 Grid Coordinate Flip under Memory Load
  {
    id: 'mem_matrix_coordinate_flip',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 3 marked cells: e.g. [0,0], [1,1], [0,2]
      // Mentally flip horizontally: cell [r, c] becomes [r, 2 - c]
      // [0,0] becomes [0,2]
      const options = [
        { id: 'A', text_ar: 'الصف الأول العمود الثالث [1 , 3]', text_en: 'Row 1 Column 3 [1 , 3]', isCorrect: true },
        { id: 'B', text_ar: 'الصف الثالث العمود الأول [3 , 1]', text_en: 'Row 3 Column 1 [3 , 1]', isCorrect: false },
        { id: 'C', text_ar: 'الصف الثاني العمود الثاني [2 , 2]', text_en: 'Row 2 Column 2 [2 , 2]', isCorrect: false },
        { id: 'D', text_ar: 'الصف الثالث العمود الثالث [3 , 3]', text_en: 'Row 3 Column 3 [3 , 3]', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تخيل شبكة مربعة 3×3 في مخيلتك:\nإذا كانت هناك نقطة مضيئة في الخلية (الصف الأول، العمود الأول [1 , 1])، وتم قلب الشبكة ذهنيًا بالكامل أفقيًا (انعكاس مرآوي لليمين واليسار حول العمود الأوسط).\nأين يصبح الموضع الجديد لهذه النقطة؟`,
        questionText_en: `Imagine a 3x3 grid in working memory:\nA target occupies cell (Row 1, Column 1). If the grid is mentally reflected horizontally (left-to-right mirror flip around the vertical center):\nWhat is the target's new position?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الانعكاس الأفقي للشبكة يبدل العمود الأول مع العمود الثالث بينما يظل الصف الأول ثابتًا، فتنتقل النقطة إلى [الصف 1 , العمود 3] (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `Horizontal matrix reflection swaps column 1 with column 3 while holding row index constant, placing the token at [Row 1, Column 3] (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 9. Semantic Category Clustering & Omission
  {
    id: 'mem_semantic_category_clustering',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الزمرد (Emerald)', text_en: 'Emerald', isCorrect: true },
        { id: 'B', text_ar: 'الأسد (Lion)', text_en: 'Lion', isCorrect: false },
        { id: 'C', text_ar: 'الياقوت (Ruby)', text_en: 'Ruby', isCorrect: false },
        { id: 'D', text_ar: 'النمر (Tiger)', text_en: 'Tiger', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `عُرضت عليك قائمة من الكلمات تنتمي لفئتين (حيوانات مفترسة ، وأحجار كريمة):\n[ أسد ، ياقوت ، نمر ، ألماس ، فهد ]\n\nإذا كان المفترض وجود 3 عناصر متكافئة لكل فئة، فما هو الحجر الكريم المفقود الذي يكمل توازن الفئتين؟`,
        questionText_en: `A mixed list contains elements from two categories (Predators and Gemstones):\n[ Lion, Ruby, Tiger, Diamond, Leopard ]\n\nIf each category requires exactly 3 balanced items, which missing gemstone balances the cluster?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `فئة المفترسات مكتملة بثلاثة عناصر (أسد، نمر، فهد). فئة الأحجار الكريمة ينقصها عنصر ثالث مكمل، والخيار الوحيد الذي يمثل حجرًا كريمًا هو "الزمرد" (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `The predator cluster has 3 items, while the gemstone cluster has only 2 (Ruby, Diamond), requiring "Emerald" to achieve parity (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 10. Phonological Loop Suppression
  {
    id: 'mem_phonological_loop_suppression',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'سيف ⬅️ حيف ⬅️ ريف ⬅️ ضيف', text_en: 'Saif ⬅️ Haif ⬅️ Reef ⬅️ Daif', isCorrect: true },
        { id: 'B', text_ar: 'سيف ⬅️ ضيف ⬅️ ريف ⬅️ حيف', text_en: 'Saif ⬅️ Daif ⬅️ Reef ⬅️ Haif', isCorrect: false },
        { id: 'C', text_ar: 'ريف ⬅️ سيف ⬅️ حيف ⬅️ ضيف', text_en: 'Reef ⬅️ Saif ⬅️ Haif ⬅️ Daif', isCorrect: false },
        { id: 'D', text_ar: 'ضيف ⬅️ ريف ⬅️ حيف ⬅️ سيف', text_en: 'Daif ⬅️ Reef ⬅️ Haif ⬅️ Saif', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احفظ الكلمات المتشابهة صوتيًا التالية بترتيبها الزمني الدقيق:\n(1) سيف ⬅️ (2) حيف ⬅️ (3) ريف ⬅️ (4) ضيف\n\nما هو الترتيب التسلسلي الدقيق غير المشوش للكلمات؟`,
        questionText_en: `Recall the acoustically similar phonological words in their exact presentation order:\n(1) Saif ⬅️ (2) Haif ⬅️ (3) Reef ⬅️ (4) Daif\n\nWhich option matches the original chronological sequence?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الترتيب الأصلي الدقيق هو (سيف ⬅️ حيف ⬅️ ريف ⬅️ ضيف)، وهو ما يمثله الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Strict phonological serial recall verifies (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 11. Feature Binding (Shape + Color Memory)
  {
    id: 'mem_feature_binding',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'المثلث الأزرق (تغير لونه إلى اللون الأحمر)', text_en: 'The blue triangle (its color altered to red)', isCorrect: true },
        { id: 'B', text_ar: 'المربع الأخضر لم يطرأ عليه أي تغيير', text_en: 'The green square remained unchanged', isCorrect: false },
        { id: 'C', text_ar: 'الدائرة الصفراء اختفت كليًا', text_en: 'The yellow circle vanished completely', isCorrect: false },
        { id: 'D', text_ar: 'النجمة البنفسجية زاد حجمها للضعف', text_en: 'The purple star doubled in size', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `عُرضت عليك 4 أشكال ملونة: [دائرة صفراء ، مربع أخضر ، مثلث أزرق ، نجمة بنفسجية].\nبعد فترة إخفاء قصيرة، ظهرت المجموعة كالتالي: [دائرة صفراء ، مربع أخضر ، مثلث أحمر ، نجمة بنفسجية].\nما هو التغيير الفردي الدقيق الذي طرأ على ارتباط الخصائص (Feature Binding)؟`,
        questionText_en: `Memorize 4 feature-bound objects: [Yellow Circle, Green Square, Blue Triangle, Purple Star].\nAfter a retention interval, the display shows: [Yellow Circle, Green Square, Red Triangle, Purple Star].\nWhat exact feature binding change occurred?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `المثلث كان في الأصل مقترنًا باللون الأزرق، ثم تحول ارتباط لونه في العرض الثاني إلى اللون الأحمر، وهو التعديل الوحيد المطابق للخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The visual feature binding linking the triangle shape to the color blue was modified to red, confirming (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 12. Spatial 2-Back Letter & Position Matching
  {
    id: 'mem_dual_nback_spatial',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'نعم، حدث تطابق تام مع الحرف الذي ظهر قبل خطوتين (2-Back Match)', text_en: 'Yes, an exact 2-back match occurred with the letter presented 2 steps prior', isCorrect: true },
        { id: 'B', text_ar: 'لا، لم يحدث أي تطابق مع الحرف السابق', text_en: 'No match occurred', isCorrect: false },
        { id: 'C', text_ar: 'التطابق حدث مع الحرف السابق مباشرة فقط (1-Back)', text_en: 'Matched only the immediate 1-back item', isCorrect: false },
        { id: 'D', text_ar: 'الحرف تكرر لأول مرة في السلسلة', text_en: 'The letter appeared for the first time', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مهمة الذاكرة العاملة N-Back بمستوى (2-Back):\nتتابعت الحروف التالية زمنيًا:\nخطوة 1: [ك] ⬅️ خطوة 2: [م] ⬅️ خطوة 3: [ك] ⬅️ خطوة 4: [ل]\n\nهل يمثل ظهور الحرف [ك] في الخطوة 3 تطابقًا صحيحًا مع قاعدة (2-Back)؟`,
        questionText_en: `In the clinical 2-Back working memory protocol, stimuli appear in sequence:\nStep 1: [K] ⬅️ Step 2: [M] ⬅️ Step 3: [K] ⬅️ Step 4: [L]\n\nDoes the appearance of [K] at Step 3 constitute a valid 2-Back match?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `بمقارنة الحرف في الخطوة 3 [ك] مع الحرف الذي سبقه بخطوتين في الخطوة 1 [ك]، نجدهما متطابقين تمامًا، مما يحقق شرط الـ 2-Back بالضرورة (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `Item at t=3 matches item at t=1 (3 - 2 = 1), fulfilling the strict 2-Back criteria in (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 13. Temporal Chronological Order Judgment
  {
    id: 'mem_temporal_order_judgment',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الحدث (ب) وقع قبل الحدث (ج) مباشرة', text_en: 'Event B occurred immediately prior to Event C', isCorrect: true },
        { id: 'B', text_ar: 'الحدث (د) وقع في البداية كأول حدث', text_en: 'Event D occurred first', isCorrect: false },
        { id: 'C', text_ar: 'الحدث (أ) وقع في نهاية التسلسل كآخر حدث', text_en: 'Event A occurred last', isCorrect: false },
        { id: 'D', text_ar: 'الأحداث وقعت كلها في وقت متزامن معًا', text_en: 'All events occurred simultaneously', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `وقعت أربعة أحداث متتالية في زمن الاختبار وفق الترتيب:\nالحدث (أ) ⬅️ ثم الحدث (ب) ⬅️ ثم الحدث (ج) ⬅️ ثم الحدث (د).\nأي من العبارات التالية تصف العلاقة الزمنية النسبية بين الأحداث بدقة متناهية؟`,
        questionText_en: `Four discrete events occurred in exact sequence: Event A ⬅️ Event B ⬅️ Event C ⬅️ Event D. Which relational temporal statement is strictly factual?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `بما أن (ب) يعقبه مباشرة (ج)، فإن الحدث (ب) يسبق (ج) بالضرورة المباشرة، وهو ما يثبت صحة العبارة في الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Event B immediately precedes Event C in the temporal timeline, verifying (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 14. Selective Inhibition under Interference
  {
    id: 'mem_selective_inhibition_span',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'س ، ص ، ط ، ع', text_en: 'S , Sh , T , A', isCorrect: true },
        { id: 'B', text_ar: '1 ، 2 ، 3 ، 4', text_en: '1 , 2 , 3 , 4', isCorrect: false },
        { id: 'C', text_ar: 'س ، 1 ، ص ، 2', text_en: 'S , 1 , Sh , 2', isCorrect: false },
        { id: 'D', text_ar: 'ط ، ع ، ص ، س', text_en: 'T , A , Sh , S', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `طُلب منك حفظ الحروف الهجائية فقط مع تجاهل الأرقام الدخيلة تمامًا أثناء القراءة:\n[ س ⬅️ 5 ⬅️ ص ⬅️ 9 ⬅️ ط ⬅️ 2 ⬅️ ع ⬅️ 7 ]\n\nما هي قائمة الحروف المستهدفة المسترجعة بالترتيب الصحيح؟`,
        questionText_en: `Retain only alphabetical letters while actively filtering and suppressing intervening numerical distractors:\n[ S ⬅️ 5 ⬅️ Sh ⬅️ 9 ⬅️ T ⬅️ 2 ⬅️ A ⬅️ 7 ]\n\nWhat is the filtered target sequence?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `بعزل الأرقام وتثبيطها في الذاكرة العاملة، تتبقى الحروف الأربعة المستهدفة مرتبة: (س ، ص ، ط ، ع)، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Suppressing numeric distractors yields strictly the letter target sequence in (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 15. Digit Span Backward Reversal
  {
    id: 'mem_digit_span_backward',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const seq = prng.sample([1, 2, 3, 4, 5, 6, 7, 8, 9], 5);
      const reversed = seq.slice().reverse().join(' - ');
      const cand = [
        seq.join(' - '),
        seq.slice().reverse().map(d => (d + 2) % 10).join(' - '),
        seq.slice().sort((a, b) => a - b).join(' - '),
        seq.slice().reverse().map(d => (d + 4) % 10).join(' - '),
        seq.slice().reverse().map(d => (d + 6) % 10).join(' - ')
      ];
      const seen = new Set([reversed]);
      const distractors = [];
      for (const d of cand) {
        if (!seen.has(d)) {
          seen.add(d);
          distractors.push(d);
          if (distractors.length === 3) break;
        }
      }

      const all = prng.shuffle([reversed, ...distractors]);
      const correctIndex = all.indexOf(reversed);

      const options = all.map((str, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: str,
        text_en: str,
        value: str
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احفظ الأرقام الخمسة التالية ثم اعكس ترتيبها ذهنيًا بالكامل من النهاية إلى البداية (Backward Digit Span):\n\n[ ${seq.join(' , ')} ]`,
        questionText_en: `Memorize the 5 digits and mentally reverse them strictly backwards (Backward Digit Span):\n\n[ ${seq.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `عكس السلسلة من اليمين لليسار يعطي تمامًا: [ ${reversed} ] (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Inverting the sequence backwards produces [ ${reversed} ] (${options[correctIndex].id}).`
      };
    }
  }
];
